import React, { useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, X, Loader2 } from 'lucide-react';
import { useQBStore } from '@/store/qbStore';
import { convertPdfToImages } from '@/lib/pdfUtils';

interface FileUploaderProps {
  files: File[];
  onAdd: (file: File) => void;
  onRemove: (index: number) => void;
  isUploading: boolean;
  label?: string;
  subLabel?: string;
  accept?: string;
  heightClass?: string;
  enablePdfToImage?: boolean;
}

export const FileUploader: React.FC<FileUploaderProps> = ({ 
  files, 
  onAdd, 
  onRemove, 
  isUploading, 
  label = "点击或拖拽上传文件",
  subLabel = "支持图片, PDF, DOC, DOCX, JSON/L, TXT, MD, TEX",
  accept = "image/*,application/pdf,.doc,.docx,.json,.jsonl,.txt,.md,.tex",
  heightClass = "h-48",
  enablePdfToImage = true
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Handle PDF conversion
      if (file.type === 'application/pdf' && enablePdfToImage) {
        setIsProcessingPdf(true);
        convertPdfToImages(file)
          .then(images => {
            images.forEach(img => onAdd(img));
          })
          .catch(err => {
            console.error('PDF conversion failed:', err);
            alert('PDF转换失败，请尝试直接上传图片');
          })
          .finally(() => {
            setIsProcessingPdf(false);
            if (inputRef.current) inputRef.current.value = '';
          });
      } else {
        onAdd(file);
        if (inputRef.current) inputRef.current.value = '';
      }
    }
  };

  return (
    <Card className="mb-4">
      <CardContent 
        className={`pt-6 flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-lg ${heightClass} bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer relative`}
        onClick={() => !isProcessingPdf && inputRef.current?.click()}
      >
        <input 
          type="file" 
          ref={inputRef} 
          className="hidden" 
          accept={accept}
          onChange={handleFileChange}
          disabled={isUploading || isProcessingPdf}
        />
        
        {isProcessingPdf ? (
          <div className="flex flex-col items-center animate-pulse">
            <Loader2 className="h-10 w-10 text-blue-500 mb-2 animate-spin" />
            <p className="text-sm text-blue-600 font-medium">正在处理 PDF...</p>
            <p className="text-xs text-blue-400 mt-1">转换页面为图片中</p>
          </div>
        ) : (
          <>
            <Upload className="h-10 w-10 text-gray-400 mb-2" />
            <p className="text-sm text-gray-500 font-medium">{label}</p>
            <p className="text-xs text-gray-400 mt-1">{subLabel}</p>
          </>
        )}
        
        {files.length > 0 && !isProcessingPdf && (
          <div className="mt-4 flex gap-2 flex-wrap justify-center w-full px-4">
            {files.map((f, i) => (
              <span key={i} className="pl-2 pr-1 py-1 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1 max-w-full truncate">
                <span className="truncate max-w-[150px]">{f.name}</span>
                <button 
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent triggering file input
                    onRemove(i);
                  }}
                  className="hover:bg-blue-200 rounded-full p-0.5 transition-colors flex-shrink-0"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const UploadZone: React.FC = () => {
  const { addFile, removeFile, files, isUploading, extractionMethod } = useQBStore();
  const isMarkdownMode = extractionMethod === 'markdown';
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium text-gray-700">题目文件</div>
      <FileUploader 
        files={files} 
        onAdd={addFile} 
        onRemove={removeFile} 
        isUploading={isUploading} 
        label="点击或拖拽上传题目文件"
        subLabel={isMarkdownMode ? "支持 PDF, DOC, DOCX, PPT, PPTX, 图片 (Markdown 模式)" : "支持图片, PDF, JSON/L, TXT, MD, TEX"}
        accept={isMarkdownMode ? "image/*,application/pdf,.doc,.docx,.ppt,.pptx,.html" : "image/*,application/pdf,.json,.jsonl,.txt,.md,.tex"}
        enablePdfToImage={!isMarkdownMode}
      />
    </div>
  );
};

export const AnswerUploadZone: React.FC = () => {
  const { addAnswerFile, removeAnswerFile, answerFiles, isUploading, extractionMethod } = useQBStore();
  const isMarkdownMode = extractionMethod === 'markdown';
  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-medium text-gray-700">答案文件 (可选)</div>
      <FileUploader 
        files={answerFiles} 
        onAdd={addAnswerFile} 
        onRemove={removeAnswerFile} 
        isUploading={isUploading} 
        label="点击或拖拽上传答案文件" 
        subLabel={isMarkdownMode ? "支持 PDF, DOC, DOCX, PPT, PPTX, 图片 (仅用于 Markdown 模式)" : "支持 PDF, 图片 (仅用于 Markdown 模式)"}
        accept="image/*,application/pdf,.doc,.docx,.ppt,.pptx,.html"
        enablePdfToImage={!isMarkdownMode}
        heightClass="h-32"
      />
    </div>
  );
};
